import {useCallback, useMemo} from 'react';

import {getSpreadsheetStoreState} from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {TableEditableSpreadsheetState} from 'notebook/tables/store/TableStore';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';

/**
 * Per-property overrides that let a caller supply a value directly instead of deriving it from
 * `SpreadsheetStore`. Each field mirrors one field of {@link TableEditableSpreadsheetState}; when
 * set it wins over the store-derived value. As more spreadsheet-derived state moves into
 * {@link TableEditableSpreadsheetState}, add the matching override here.
 */
export interface TableSpreadsheetStoreOverrides {
  overrideShowExcessLabels?: boolean;
}

/**
 * Derives the {@link TableEditableSpreadsheetState} that {@link TableModelProvider} pushes onto the
 * table store from `SpreadsheetStore`, keeping that subscription logic out of the provider. Each
 * piece of state is sourced from the spreadsheet store (scoped to `tableNamespace`) and can be
 * overridden by the caller via `overrides`.
 *
 * Isolating this here means a formula interaction re-renders only the axis-cell models (the sole
 * readers of `spreadsheetState`) rather than the whole table.
 */
export function useTableSpreadsheetStore(
  tableNamespace: string,
  disableFormulas: boolean,
  {overrideShowExcessLabels}: TableSpreadsheetStoreOverrides
): TableEditableSpreadsheetState {
  // `showExcessLabels` is derived from whether a formula is being edited (scoped to this table's
  // namespace); see `getSpreadsheetStoreState`.
  const selectShowExcessLabels = useCallback(
    (): boolean =>
      // Namespace-scoped only — NOT table/document-scoped. `showExcessLabels` is a per-namespace
      // signal (a formula being edited anywhere in the namespace turns it on for every table);
      // passing a `tableName`/`documentName` would make `getState` return an empty formula for every
      // table other than the actively-edited one, incorrectly forcing `showExcessLabels` false there.
      // `disableFormulas` mirrors the HOC's per-editor `hasFormulasDisabled`, which gates it off.
      getSpreadsheetStoreState(tableNamespace, disableFormulas).showExcessLabels,
    [tableNamespace, disableFormulas]
  );
  const showExcessLabelsFromStore = useStoreSelector(SpreadsheetStore, selectShowExcessLabels);
  const showExcessLabels = overrideShowExcessLabels ?? showExcessLabelsFromStore;

  return useMemo<TableEditableSpreadsheetState>(() => ({showExcessLabels}), [showExcessLabels]);
}
